import { alpha } from '@mui/material/styles';

const pickColor = (...values) => (
  values.find((value) => typeof value === 'string' && value.trim().length > 0) || '#6aa7ff'
);

const resolveReportPalette = (theme) => {
  const primaryMain = pickColor(theme?.palette?.primary?.main, '#6aa7ff');
  const primaryLight = pickColor(theme?.palette?.primary?.light, '#c7e4ff', primaryMain);
  const secondaryMain = pickColor(theme?.palette?.secondary?.main, '#8fd8ff', primaryMain);
  const infoMain = pickColor(theme?.palette?.info?.main, '#9edcff', secondaryMain, primaryMain);
  const backgroundPaper = pickColor(theme?.palette?.background?.paper, '#ffffff');
  const backgroundDefault = pickColor(theme?.palette?.background?.default, '#f7fbff');
  const textPrimary = pickColor(theme?.palette?.text?.primary, '#1f2937');

  return {
    primaryMain,
    primaryLight,
    secondaryMain,
    infoMain,
    backgroundPaper,
    backgroundDefault,
    textPrimary
  };
};

export const getReportPageShellSx = () => ({
  position: 'relative',
  overflow: 'hidden',
  px: { xs: 1, md: 1.5 },
  pb: 2.5,
  pt: 0.5,
  minHeight: '100%',
});

export const getReportCanvasSx = (theme) => {
  const palette = resolveReportPalette(theme);

  return {
    position: 'relative',
    display: 'grid',
    gap: 2,
    p: { xs: 1.5, sm: 2 },
    borderRadius: 4,
    overflow: 'hidden',
    background: `linear-gradient(180deg, ${alpha(palette.primaryLight, 0.08)} 0%, ${alpha(palette.backgroundDefault, 0.96)} 100%)`,
  };
};

export const getReportBackdropSx = (theme) => {
  const palette = resolveReportPalette(theme);

  return {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    background: `
      radial-gradient(circle at top left, ${alpha(palette.primaryMain, 0.16)} 0%, transparent 42%),
      radial-gradient(circle at bottom right, ${alpha(palette.secondaryMain, 0.12)} 0%, transparent 38%)
    `,
  };
};

export const getPlainSurfaceCardSx = (theme) => {
  const palette = resolveReportPalette(theme);

  return {
    position: 'relative',
    borderRadius: 3,
    border: `1px solid ${alpha(palette.primaryMain, 0.14)}`,
    backgroundColor: alpha(palette.backgroundPaper, 0.94),
    boxShadow: `0 18px 45px ${alpha(palette.primaryMain, 0.08)}`,
  };
};

export const getReportSurfaceCardSx = (theme) => {
  const palette = resolveReportPalette(theme);

  return {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: { xs: 3, md: 4 },
    border: `1px solid ${alpha(palette.primaryMain, 0.14)}`,
    boxShadow: `0 24px 60px ${alpha(palette.primaryMain, 0.12)}`,
    backdropFilter: 'blur(18px)',
    background: `linear-gradient(180deg, ${alpha('#ffffff', 0.84)} 0%, ${alpha('#ffffff', 0.68)} 100%)`,
  };
};

export const getReportHeroCardSx = (theme) => {
  const palette = resolveReportPalette(theme);

  return {
    ...getReportSurfaceCardSx(theme),
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      background: `linear-gradient(135deg, ${alpha(palette.primaryMain, 0.16)} 0%, ${alpha(palette.secondaryMain, 0.18)} 100%)`,
      pointerEvents: 'none',
    },
  };
};

export const getReportMetricTileSx = (theme) => {
  const palette = resolveReportPalette(theme);

  return {
    borderRadius: 3,
    border: `1px solid ${alpha(palette.primaryMain, 0.12)}`,
    background: `linear-gradient(160deg, ${alpha('#ffffff', 0.78)} 0%, ${alpha('#ffffff', 0.54)} 100%)`,
    backdropFilter: 'blur(16px)',
    boxShadow: `0 16px 36px ${alpha(palette.primaryMain, 0.08)}`,
    p: 1.5,
    display: 'grid',
    gap: 0.4,
  };
};

export const getReportTabsShellSx = (theme) => ({
  ...getReportSurfaceCardSx(theme),
  px: { xs: 1, md: 1.5 },
  py: 0.5,
});

export const getExecutiveGlassBackdropSx = (theme) => {
  const palette = resolveReportPalette(theme);

  return {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    background: `
      radial-gradient(circle at 0% 0%, ${alpha(palette.primaryLight, 0.28)} 0%, transparent 28%),
      radial-gradient(circle at 100% 0%, ${alpha(palette.secondaryMain, 0.18)} 0%, transparent 34%),
      radial-gradient(circle at 50% 100%, ${alpha(palette.infoMain, 0.12)} 0%, transparent 42%),
      linear-gradient(180deg, ${alpha('#ffffff', 0.98)} 0%, ${alpha('#f4faff', 0.96)} 46%, ${alpha('#ffffff', 0.99)} 100%)
    `,
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      backgroundImage: `
        linear-gradient(${alpha(palette.primaryMain, 0.05)} 1px, transparent 1px),
        linear-gradient(90deg, ${alpha(palette.primaryMain, 0.05)} 1px, transparent 1px)
      `,
      backgroundSize: '38px 38px',
      opacity: 0.45,
      maskImage: 'linear-gradient(180deg, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.3) 72%, transparent 100%)'
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: '12% 8%',
      borderRadius: '50%',
      background: `radial-gradient(circle, ${alpha('#ffffff', 0.72)} 0%, transparent 62%)`,
      filter: 'blur(54px)',
      opacity: 0.95
    }
  };
};

export const getExecutiveGlassCardSx = (theme) => {
  const palette = resolveReportPalette(theme);

  return {
    ...getReportSurfaceCardSx(theme),
    borderRadius: { xs: 4, md: 5 },
    border: `1px solid ${alpha(palette.primaryMain, 0.1)}`,
    boxShadow: `
      0 24px 60px ${alpha(palette.primaryMain, 0.09)},
      0 8px 24px ${alpha(palette.secondaryMain, 0.05)}
    `,
    background: `linear-gradient(180deg, ${alpha('#ffffff', 0.88)} 0%, ${alpha('#ffffff', 0.6)} 100%)`,
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 1,
      borderRadius: 'inherit',
      pointerEvents: 'none',
      background: `linear-gradient(180deg, ${alpha('#ffffff', 0.34)} 0%, transparent 34%, transparent 100%)`
    }
  };
};

export const getExecutiveGlassHeroSx = (theme) => {
  const palette = resolveReportPalette(theme);

  return {
    ...getExecutiveGlassCardSx(theme),
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      background: `
        radial-gradient(circle at top right, ${alpha('#ffffff', 0.42)} 0%, transparent 24%),
        linear-gradient(135deg, ${alpha(palette.primaryMain, 0.16)} 0%, ${alpha(palette.secondaryMain, 0.14)} 50%, ${alpha('#9edcff', 0.18)} 100%)
      `
    }
  };
};

export const getExecutiveGlassKpiSx = (theme) => {
  const palette = resolveReportPalette(theme);

  return {
    borderRadius: { xs: 3.25, md: 4 },
    border: `1px solid ${alpha(palette.primaryMain, 0.1)}`,
    background: `linear-gradient(160deg, ${alpha('#ffffff', 0.82)} 0%, ${alpha('#ffffff', 0.58)} 100%)`,
    backdropFilter: 'blur(16px)',
    boxShadow: `0 16px 34px ${alpha(palette.primaryMain, 0.07)}`,
    p: 1.5,
    display: 'grid',
    gap: 0.45,
  };
};

export const getExecutiveGlassPillSx = (theme) => {
  const palette = resolveReportPalette(theme);

  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.55,
    px: 1.2,
    py: 0.75,
    borderRadius: 999,
    border: `1px solid ${alpha(palette.primaryMain, 0.12)}`,
    background: `linear-gradient(180deg, ${alpha(theme.palette.secondary.main, 0.86)} 0%, ${alpha(theme.palette.secondary.main, 0.58)} 100%)`,
    boxShadow: `0 12px 28px ${alpha(palette.primaryMain, 0.06)}`,
    backdropFilter: 'blur(14px)'
  };
};

export const getExecutiveGlassTabsShellSx = (theme) => ({
  ...getExecutiveGlassCardSx(theme),
  px: { xs: 0.85, md: 1.1 },
  py: 0.75,
});

export const getExecutiveGlassTabsSx = (theme) => {
  const palette = resolveReportPalette(theme);

  return {
    minHeight: 46,
    flex: 1,
    minWidth: 0,
    '& .MuiTabs-indicator': {
      display: 'none'
    },
    '& .MuiTabs-flexContainer': {
      flexWrap: 'wrap',
      gap: 0.55
    },
    '& .MuiTab-root': {
      minHeight: 42,
      px: 1.6,
      py: 0.7,
      borderRadius: 999,
      border: `1px solid ${alpha(palette.primaryMain, 0)}`,
      textTransform: 'none',
      fontWeight: 700,
      letterSpacing: '0.01em',
      color: alpha(palette.textPrimary, 0.64),
      transition: 'all 0.2s ease',
      '&:hover': {
        color: palette.textPrimary,
        background: `linear-gradient(180deg, ${alpha('#ffffff', 0.74)} 0%, ${alpha('#ffffff', 0.48)} 100%)`,
        borderColor: alpha(palette.primaryMain, 0.1)
      }
    },
    '& .MuiTab-root.Mui-selected': {
      color: palette.textPrimary,
      background: `linear-gradient(180deg, ${alpha('#ffffff', 0.92)} 0%, ${alpha('#ffffff', 0.62)} 100%)`,
      borderColor: alpha(palette.primaryMain, 0.14),
      boxShadow: `0 14px 28px ${alpha(palette.primaryMain, 0.09)}`
    },
    '& .MuiTab-root.Mui-focusVisible': {
      outline: `2px solid ${alpha(palette.primaryMain, 0.18)}`,
      outlineOffset: 2
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: 999,
      background: `linear-gradient(180deg, ${alpha('#ffffff', 0.88)} 0%, ${alpha('#ffffff', 0.62)} 100%)`,
      backdropFilter: 'blur(14px)',
      boxShadow: `0 14px 28px ${alpha(palette.primaryMain, 0.08)}`,
      '& fieldset': {
        borderColor: alpha(palette.primaryMain, 0.12)
      },
      '&:hover fieldset': {
        borderColor: alpha(palette.primaryMain, 0.18)
      },
      '&.Mui-focused fieldset': {
        borderColor: alpha(palette.primaryMain, 0.22)
      }
    },
    '& .MuiSelect-select': {
      py: 1.05,
      px: 1.45,
      fontWeight: 700,
      color: alpha(palette.textPrimary, 0.88)
    }
  };
};

export const getExecutiveGlassButtonSx = (theme) => {
  const palette = resolveReportPalette(theme);

  return {
    minHeight: 40,
    px: 1.5,
    borderRadius: 999,
    textTransform: 'none',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    borderColor: alpha(palette.primaryMain, 0.16),
    color: palette.textPrimary,
    background: `linear-gradient(180deg, ${alpha('#ffffff', 0.82)} 0%, ${alpha('#ffffff', 0.54)} 100%)`,
    backdropFilter: 'blur(12px)',
    boxShadow: `0 14px 28px ${alpha(palette.primaryMain, 0.07)}`,
    '&:hover': {
      borderColor: alpha(palette.primaryMain, 0.22),
      background: `linear-gradient(180deg, ${alpha('#ffffff', 0.9)} 0%, ${alpha('#ffffff', 0.62)} 100%)`
    }
  };
};

export const getExecutiveGlassControlClusterSx = (theme) => {
  const palette = resolveReportPalette(theme);

  return {
    display: 'flex',
    alignItems: 'center',
    gap: 0.8,
    flexWrap: 'wrap',
    px: 0.8,
    py: 0.65,
    borderRadius: 999,
    border: `1px solid ${alpha(palette.primaryMain, 0.12)}`,
    background: `linear-gradient(180deg, ${alpha('#ffffff', 0.88)} 0%, ${alpha('#ffffff', 0.62)} 100%)`,
    backdropFilter: 'blur(18px)',
    boxShadow: `
      0 18px 34px ${alpha(palette.primaryMain, 0.08)},
      0 6px 18px ${alpha(palette.secondaryMain, 0.05)}
    `,
  };
};

export const getExecutiveGlassIconButtonSx = (theme) => {
  const palette = resolveReportPalette(theme);

  return {
    width: 42,
    height: 42,
    borderRadius: 999,
    border: `1px solid ${alpha(palette.primaryMain, 0.12)}`,
    background: `linear-gradient(180deg, ${alpha('#ffffff', 0.9)} 0%, ${alpha('#ffffff', 0.64)} 100%)`,
    backdropFilter: 'blur(14px)',
    boxShadow: `0 14px 30px ${alpha(palette.primaryMain, 0.08)}`,
    color: alpha(palette.textPrimary, 0.84),
    transition: 'all 0.2s ease',
    '&:hover': {
      background: `linear-gradient(180deg, ${alpha('#ffffff', 0.98)} 0%, ${alpha('#ffffff', 0.72)} 100%)`,
      borderColor: alpha(palette.primaryMain, 0.2),
      transform: 'translateY(-1px)',
    },
  };
};

export const getExecutiveGlassFieldSx = (theme) => {
  const palette = resolveReportPalette(theme);

  return {
    '& .MuiInputLabel-root': {
      fontWeight: 700,
      color: alpha(palette.textPrimary, 0.62),
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: alpha(palette.textPrimary, 0.82),
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: 3,
      background: `linear-gradient(180deg, ${alpha('#ffffff', 0.9)} 0%, ${alpha('#ffffff', 0.66)} 100%)`,
      backdropFilter: 'blur(14px)',
      boxShadow: `0 14px 28px ${alpha(palette.primaryMain, 0.07)}`,
      '& fieldset': {
        borderColor: alpha(palette.primaryMain, 0.12),
      },
      '&:hover fieldset': {
        borderColor: alpha(palette.primaryMain, 0.18),
      },
      '&.Mui-focused fieldset': {
        borderColor: alpha(palette.primaryMain, 0.24),
      },
    },
    '& .MuiOutlinedInput-input': {
      fontWeight: 600,
    },
    '& .MuiSvgIcon-root': {
      color: alpha(palette.textPrimary, 0.72),
    },
    '& .MuiFormHelperText-root': {
      marginLeft: 0.75,
      marginRight: 0.75,
    },
  };
};

export const getExecutiveGlassAutocompleteSx = (theme) => {
  const palette = resolveReportPalette(theme);

  return {
    ...getExecutiveGlassFieldSx(theme),
    '& .MuiAutocomplete-tag': {
      ...getHeroChipSx(theme),
      maxWidth: '100%',
      my: 0.25,
      borderRadius: 999,
    },
    '& .MuiChip-label': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
      color: alpha(palette.textPrimary, 0.68),
    },
  };
};

export const getExecutiveGlassPopperSx = (theme) => {
  const palette = resolveReportPalette(theme);
  const paperStyles = {
    ...getExecutiveGlassCardSx(theme),
    mt: 0.8,
    p: 0.5,
    borderRadius: 3.25,
  };

  return {
    ...paperStyles,
    '& .MuiPaper-root': paperStyles,
    '& .MuiAutocomplete-listbox': {
      p: 0.35,
      display: 'grid',
      gap: 0.2,
    },
    '& .MuiAutocomplete-option': {
      minHeight: 42,
      borderRadius: 2,
      color: alpha(palette.textPrimary, 0.86),
      transition: 'all 0.18s ease',
      '&.Mui-focused': {
        background: alpha(palette.primaryMain, 0.08),
      },
      '&[aria-selected="true"]': {
        background: alpha(palette.primaryMain, 0.14),
        color: palette.textPrimary,
      },
    },
  };
};

export const getExecutiveGlassTableHeadCellSx = (theme) => {
  const palette = resolveReportPalette(theme);

  return {
    fontWeight: 800,
    fontSize: '0.72rem',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: alpha(palette.textPrimary, 0.76),
    bgcolor: alpha(palette.primaryMain, 0.06),
    borderBottom: `1px solid ${alpha(palette.primaryMain, 0.1)}`
  };
};

export const getHeroChipSx = (theme, accent = false) => {
  const palette = resolveReportPalette(theme);

  return {
    ...getExecutiveGlassPillSx(theme),
    py: 0.55,
    px: 1.05,
    fontWeight: 700,
    borderColor: alpha(accent ? palette.secondaryMain : palette.primaryMain, 0.16),
    background: accent
      ? `linear-gradient(180deg, ${alpha(palette.secondaryMain, 0.2)} 0%, ${alpha('#ffffff', 0.72)} 100%)`
      : `linear-gradient(180deg, ${alpha('#ffffff', 0.84)} 0%, ${alpha('#ffffff', 0.62)} 100%)`,
    color: accent ? palette.secondaryMain : alpha(palette.textPrimary, 0.86)
  };
};

export const getMetricGridSx = () => ({
  '& > *': {
    minWidth: 0
  },
  '& .MuiCard-root': {
    height: '100%'
  }
});

export const getReportHeroSx = (theme) => ({
  ...getExecutiveGlassHeroSx(theme),
  position: 'relative',
  p: { xs: 1.75, md: 2.2 },
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 1.5,
  flexWrap: 'wrap'
});

export const getReportTabsRailSx = (theme) => ({
  ...getExecutiveGlassTabsShellSx(theme),
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  flexWrap: 'wrap'
});

export const getReportTabsSx = (theme) => ({
  ...getExecutiveGlassTabsSx(theme)
});

export const getSectionCardSx = (theme) => ({
  ...getExecutiveGlassCardSx(theme)
});
